import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  Check,
  Copy,
  GraduationCap,
  MapPin,
  MessageSquare,
  Presentation,
  QrCode,
  Send,
  User,
} from 'lucide-react';
import {
  APP_STORE_URL,
  BETA_PROGRAM,
  CONTACT_EMAIL,
  FOUNDER_INTRO,
  PRIVACY_PATH,
  SCHOOL_FONT_HREF,
  SITE_NAME,
  getSchoolBySlug,
  schoolCanonicalUrl,
  schoolHeadMeta,
  type SchoolConfig,
} from '../../content/schools';
import { readAttribution, trackOutreachEvent } from '../../utils/outreachApi';
import { BetaApplicationForm } from './BetaApplicationForm';
import { SchoolInquiryForm } from './SchoolInquiryForm';
import './school.css';

/** Keeps runtime head tags in step with the static HTML generated at build time. */
function useSchoolHead(school: SchoolConfig) {
  useEffect(() => {
    const { title, canonical, meta } = schoolHeadMeta(school);
    document.title = title;

    const upsert = (selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) => {
      let el = document.head.querySelector<HTMLElement>(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      apply(el);
    };

    for (const tag of meta) {
      const attr = tag.property ? 'property' : 'name';
      const key = (tag.property ?? tag.name)!;
      upsert(`meta[${attr}="${key}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute(attr, key);
        return m;
      }, el => el.setAttribute('content', tag.content));
    }
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.rel = 'canonical';
      return l;
    }, el => el.setAttribute('href', canonical));
    upsert(`link[href="${SCHOOL_FONT_HREF}"]`, () => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = SCHOOL_FONT_HREF;
      return l;
    }, () => {});
  }, [school]);
}

function Logo() {
  return <img src="/help-from-sky-logo.png" alt={SITE_NAME} className="sl-logo" width={111} height={50} />;
}

export function SchoolLandingPage() {
  const { slug } = useParams();
  const school = getSchoolBySlug(slug);
  if (!school) return <Navigate to="/" replace />;
  return <SchoolLanding key={school.slug} school={school} />;
}

function SchoolLanding({ school }: { school: SchoolConfig }) {
  const location = useLocation();
  const attribution = useMemo(
    () => readAttribution(school, location.search, location.pathname),
    // First-touch for this page load; in-page hash jumps shouldn't change it.
    [school],
  );
  const [betaOpen, setBetaOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useSchoolHead(school);

  useEffect(() => {
    trackOutreachEvent('page_view', school.slug);
  }, [school.slug]);

  // The SPA renders after the browser's initial anchor jump, so honour deep links (#beta, #partner) here.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  const trackStudentCta = () => trackOutreachEvent('student_cta_click', school.slug);
  const trackPartnerCta = () => trackOutreachEvent('partner_cta_click', school.slug);

  const verifiedPrograms = school.programs.filter(p => p.verified);
  const betaDetails = [
    { label: 'What you get', value: BETA_PROGRAM.offer },
    { label: 'Who can apply', value: BETA_PROGRAM.eligibility },
    { label: 'Time commitment', value: BETA_PROGRAM.timeCommitment },
    { label: 'Cost', value: BETA_PROGRAM.cost },
  ].filter((d): d is { label: string; value: string } => Boolean(d.value));

  return (
    <div className="sl-page">
      <a href="#main" className="sl-skip">Skip to content</a>

      <header className="sl-header">
        <div className="sl-container sl-header-inner">
          <a href="/" className="sl-logo-link" aria-label={`${SITE_NAME} home`}>
            <Logo />
          </a>
          <nav aria-label="Page sections" className="sl-nav">
            <a href="#beta" onClick={trackStudentCta}>Students</a>
            <a href="#partner" onClick={trackPartnerCta}>Schools</a>
            <a href="/" className="sl-nav-main">helpfromsky.com</a>
          </nav>
        </div>
      </header>

      <main id="main">
        {/* B — Hero */}
        <section className="sl-hero" aria-labelledby="sl-hero-title">
          <div className="sl-container sl-hero-inner">
            <p className="sl-eyebrow"><span aria-hidden="true" />A resource for future beauty entrepreneurs</p>
            <h1 id="sl-hero-title" className="sl-display">
              Learn your craft. <em>Build your business.</em>
            </h1>
            <p className="sl-lede">
              Help from Sky is building simple business-management software for independent service
              professionals. We’re inviting aspiring beauty professionals to help shape a tool for managing
              appointments, organizing client information, and building lasting client relationships.
            </p>
            <p className="sl-hero-school">
              <GraduationCap aria-hidden="true" size={18} />
              <span>For students and educators at <strong>{school.name}</strong></span>
            </p>
            <div className="sl-hero-ctas">
              <a href="#beta" className="sl-btn sl-btn-primary" onClick={trackStudentCta}>
                Explore the Beta Program <ArrowRight aria-hidden="true" size={18} />
              </a>
              <a href="#partner" className="sl-btn sl-btn-secondary" onClick={trackPartnerCta}>
                Interested in Partnering?
              </a>
            </div>
          </div>
        </section>

        {/* C — School-specific introduction */}
        <section className="sl-section" aria-labelledby="sl-school-title">
          <div className="sl-container">
            <div className="sl-school-card">
              <div className="sl-school-head">
                <p className="sl-label">Why we’re reaching out</p>
                <h2 id="sl-school-title" className="sl-school-name">{school.name}</h2>
                <p className="sl-school-loc">
                  <MapPin aria-hidden="true" size={16} /> {school.location}
                </p>
              </div>
              <div className="sl-school-body">
                <p>{school.intro}</p>
                {verifiedPrograms.length > 0 && (
                  <p className="sl-programs">
                    <span className="sl-programs-label">Programs:</span>{' '}
                    {verifiedPrograms.map(p => p.name).join(' · ')}
                  </p>
                )}
                <p className="sl-disclaimer">
                  This page was created by {SITE_NAME}, an independent company. It isn’t affiliated with or
                  endorsed by {school.name}.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* D — Student benefits */}
        <section className="sl-section" aria-labelledby="sl-benefits-title">
          <div className="sl-container">
            <p className="sl-label">For students</p>
            <h2 id="sl-benefits-title" className="sl-title">Build good business habits from your first client.</h2>
            <div className="sl-benefits">
              <article className="sl-benefit">
                <div className="sl-icon"><CalendarDays aria-hidden="true" /></div>
                <h3>Manage appointments</h3>
                <p>
                  Keep bookings organized as you begin taking clients—with day, week, and month calendar views
                  and recurring appointments for your regulars.
                </p>
              </article>
              <article className="sl-benefit">
                <div className="sl-icon"><User aria-hidden="true" /></div>
                <h3>Keep client details together</h3>
                <p>
                  Build organized records for the people you serve: notes, visit history, and preferences in one
                  place.
                </p>
              </article>
              <article className="sl-benefit">
                <div className="sl-icon"><MessageSquare aria-hidden="true" /></div>
                <h3>Encourage repeat bookings</h3>
                <p>
                  Develop consistent client follow-up habits with ready-to-send reminder and follow-up message
                  templates.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* E — Beta program */}
        <section id="beta" className="sl-section sl-anchor" aria-labelledby="sl-beta-title">
          <div className="sl-container sl-split">
            <div className="sl-split-copy">
              <p className="sl-label">Beta program</p>
              <h2 id="sl-beta-title" className="sl-title">
                Help shape a tool built for independent beauty professionals.
              </h2>
              <p className="sl-body">
                We’re recruiting a small group of students and recent graduates who expect to begin taking
                clients soon—or who have recently started working independently.
              </p>
              <p className="sl-body">{school.studentMessage}</p>
              <ul className="sl-checks">
                <li><Check aria-hidden="true" size={18} /> Students preparing to take their first paying clients</li>
                <li><Check aria-hidden="true" size={18} /> Recent graduates who’ve started working independently</li>
                <li><Check aria-hidden="true" size={18} /> People willing to share honest feedback on what works</li>
              </ul>
            </div>

            <div className="sl-panel">
              {betaDetails.length > 0 ? (
                <dl className="sl-details">
                  {betaDetails.map(d => (
                    <div key={d.label}>
                      <dt>{d.label}</dt>
                      <dd>{d.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="sl-panel-note">
                  We’ll confirm exactly what’s involved—including the expected time commitment—with you directly
                  before you decide to take part. Applying doesn’t commit you to anything.
                </p>
              )}

              {!betaOpen && (
                <button
                  type="button"
                  className="sl-btn sl-btn-primary sl-btn-block"
                  aria-expanded={betaOpen}
                  aria-controls="beta-application"
                  onClick={() => {
                    trackStudentCta();
                    setBetaOpen(true);
                  }}
                >
                  Apply for Beta Access <ArrowRight aria-hidden="true" size={18} />
                </button>
              )}
              <RevealRegion id="beta-application" open={betaOpen} focusId="firstName" title="Beta application">
                <BetaApplicationForm school={school} attribution={attribution} />
              </RevealRegion>
            </div>
          </div>
        </section>

        {/* F — School partnership */}
        <section id="partner" className="sl-section sl-anchor" aria-labelledby="sl-partner-title">
          <div className="sl-container">
            <div className="sl-partner">
              <p className="sl-label">For schools</p>
              <h2 id="sl-partner-title" className="sl-title">An optional resource for your students</h2>
              <p className="sl-body">
                We’d love to explore sharing this beta opportunity with students preparing for independent work.
                Schools can participate by forwarding an invitation, sharing a QR code, or discussing a brief
                introduction to the business side of starting a beauty career.
              </p>
              <p className="sl-body">{school.administratorMessage}</p>

              <ul className="sl-ways">
                <li>
                  <Send aria-hidden="true" />
                  <span><strong>Forward an invitation</strong> to students who are getting ready to take clients.</span>
                </li>
                <li>
                  <QrCode aria-hidden="true" />
                  <span><strong>Share a QR code</strong> that links to this page—we can provide one.</span>
                </li>
                <li>
                  <Presentation aria-hidden="true" />
                  <span><strong>Discuss a brief introduction</strong> to the business side of a beauty career.</span>
                </li>
              </ul>

              <div className="sl-reassure">
                <p>
                  <Check aria-hidden="true" size={18} />
                  Starting a conversation doesn’t require any curriculum changes or institutional commitment.
                </p>
                <p>
                  <Check aria-hidden="true" size={18} />
                  We never ask schools for student contact lists. Students choose to apply themselves through
                  this page.
                </p>
              </div>

              <ShareLink url={schoolCanonicalUrl(school.slug)} />

              <div className="sl-partner-cta">
                <p className="sl-partner-lead">{school.contactCta}</p>
                {!inquiryOpen && (
                  <button
                    type="button"
                    className="sl-btn sl-btn-primary"
                    aria-expanded={inquiryOpen}
                    aria-controls="school-inquiry"
                    onClick={() => {
                      trackPartnerCta();
                      setInquiryOpen(true);
                    }}
                  >
                    Discuss a Partnership <ArrowRight aria-hidden="true" size={18} />
                  </button>
                )}
              </div>
              <RevealRegion id="school-inquiry" open={inquiryOpen} focusId="name" title="Partnership inquiry">
                <div className="sl-panel sl-panel-flat">
                  <SchoolInquiryForm school={school} attribution={attribution} />
                </div>
              </RevealRegion>
            </div>
          </div>
        </section>

        {/* G — About */}
        <section className="sl-section" aria-labelledby="sl-about-title">
          <div className="sl-container sl-about">
            <div>
              <p className="sl-label">About {SITE_NAME}</p>
              <h2 id="sl-about-title" className="sl-title">Built for people who run a business on their own.</h2>
              <p className="sl-body">
                {SITE_NAME} is an iPhone app for independent beauty and wellness professionals. It brings your
                calendar, client profiles, and reminder and follow-up templates together in one place—and it’s
                privacy-first, storing your client data on your device by default.
              </p>
              <p className="sl-body">
                It’s free to download from the App Store, so you can look around before you apply.
              </p>
              <a href={APP_STORE_URL} className="sl-text-link" target="_blank" rel="noopener noreferrer">
                See screenshots on the App Store <ArrowRight aria-hidden="true" size={16} />
              </a>
            </div>
            {FOUNDER_INTRO && (
              <figure className="sl-founder">
                <blockquote>{FOUNDER_INTRO.message}</blockquote>
                <figcaption>
                  <strong>{FOUNDER_INTRO.name}</strong>, {FOUNDER_INTRO.role}
                </figcaption>
              </figure>
            )}
          </div>
        </section>
      </main>

      {/* H — Footer */}
      <footer className="sl-footer">
        <div className="sl-container sl-footer-inner">
          <a href="/" className="sl-logo-link" aria-label={`${SITE_NAME} home`}>
            <Logo />
          </a>
          <nav aria-label="Footer" className="sl-footer-links">
            <a href={PRIVACY_PATH}>Privacy</a>
            <a href={`mailto:${CONTACT_EMAIL}`}>Contact</a>
          </nav>
          <p className="sl-footer-note">
            © {new Date().getFullYear()} {SITE_NAME}. {SITE_NAME} is independent and is not affiliated with or
            endorsed by the schools mentioned on this site.
          </p>
        </div>
      </footer>
    </div>
  );
}

/** Inline form container: mounts on open and moves focus to its first field. */
function RevealRegion({
  id, open, focusId, title, children,
}: {
  id: string;
  open: boolean;
  focusId: string;
  title: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (open) document.getElementById(focusId)?.focus();
  }, [open, focusId]);

  return (
    <div id={id} role="region" aria-label={title} hidden={!open} className="sl-reveal">
      {open && children}
    </div>
  );
}

function ShareLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const display = url.replace(/^https:\/\//, '');

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="sl-share">
      <span className="sl-share-label">Link to share with students</span>
      <div className="sl-share-row">
        <code className="sl-share-url">{display}</code>
        <button type="button" className="sl-btn sl-btn-ghost" onClick={copy}>
          {copied ? <Check aria-hidden="true" size={16} /> : <Copy aria-hidden="true" size={16} />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <span className="sl-visually-hidden" aria-live="polite">{copied ? 'Link copied to clipboard' : ''}</span>
    </div>
  );
}
